/**
 * Dynamic favicon generator for SaaS tenants
 * Generates favicons based on store name/domain
 */

export interface FaviconOptions {
  text: string;
  backgroundColor?: string;
  textColor?: string;
  size?: number;
}

export function generateFavicon(options: FaviconOptions): string {
  const {
    text,
    backgroundColor = '#3B82F6',
    textColor = '#FFFFFF',
    size = 32
  } = options;

  // Get first letter or first two characters
  const displayText = text.length > 2 ? text.substring(0, 2).toUpperCase() : text.toUpperCase();
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  
  if (!ctx) {
    return '';
  }

  canvas.width = size;
  canvas.height = size;

  // Draw background circle
  ctx.fillStyle = backgroundColor;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  ctx.fill();

  // Draw text
  ctx.fillStyle = textColor;
  ctx.font = `bold ${size * 0.4}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(displayText, size / 2, size / 2);

  return canvas.toDataURL('image/png');
}

export function setDynamicFavicon(storeName: string, storeDomain?: string): void {
  try {
    // Use domain if available, otherwise use store name
    const faviconText = storeDomain ? 
      storeDomain.split('.')[0] : 
      storeName;

    const storeId = localStorage.getItem('storeId') || '2';
    
    // Generate multiple favicon sizes for different use cases
    const faviconSizes = [
      { size: 16, rel: 'icon', type: 'image/svg+xml', format: 'svg' },
      { size: 32, rel: 'icon', type: 'image/svg+xml', format: 'svg' },
      { size: 48, rel: 'icon', type: 'image/svg+xml', format: 'svg' },
      { size: 64, rel: 'icon', type: 'image/svg+xml', format: 'svg' },
      { size: 128, rel: 'apple-touch-icon', type: 'image/svg+xml', format: 'svg' },
      { size: 192, rel: 'apple-touch-icon', type: 'image/svg+xml', format: 'svg' },
      { size: 512, rel: 'apple-touch-icon', type: 'image/svg+xml', format: 'svg' }
    ];

    // Update or create favicon links
    faviconSizes.forEach(({ size, rel, type, format }) => {
      let faviconLink = document.querySelector(`link[rel="${rel}"][sizes="${size}x${size}"]`) as HTMLLinkElement;
      
      if (!faviconLink) {
        faviconLink = document.createElement('link');
        faviconLink.rel = rel;
        faviconLink.sizes = `${size}x${size}`;
        faviconLink.type = type;
        document.head.appendChild(faviconLink);
      }

      // Use server-side generated favicon
      const serverFaviconUrl = `${import.meta.env.VITE_API_ENDPOINT}/favicon/${storeId}?text=${encodeURIComponent(faviconText)}&bg=%233B82F6&color=%23FFFFFF&size=${size}&format=${format}`;
      faviconLink.href = serverFaviconUrl;
    });

    // Update manifest.json if it exists
    updateManifestIcons(storeId, faviconText);

    // Update meta tags for social sharing
    updateMetaTags(storeId, faviconText, storeName);

  } catch (error) {
    console.warn('Failed to generate dynamic favicon:', error);
  }
}

function updateManifestIcons(storeId: string, faviconText: string): void {
  try {
    const manifestLink = document.querySelector('link[rel="manifest"]') as HTMLLinkElement;
    if (manifestLink) {
      // Fetch and update manifest.json
      fetch(manifestLink.href)
        .then(response => response.json())
        .then(manifest => {
          const iconSizes = [192, 512];
          manifest.icons = iconSizes.map(size => ({
            src: `${import.meta.env.VITE_API_ENDPOINT}/favicon/${storeId}?text=${encodeURIComponent(faviconText)}&bg=%233B82F6&color=%23FFFFFF&size=${size}&format=png`,
            sizes: `${size}x${size}`,
            type: 'image/png',
            purpose: 'any maskable'
          }));
          
          // Update manifest link with new data
          const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
          const manifestUrl = URL.createObjectURL(manifestBlob);
          manifestLink.href = manifestUrl;
        })
        .catch(error => {
          console.warn('Failed to update manifest:', error);
        });
    }
  } catch (error) {
    console.warn('Failed to update manifest icons:', error);
  }
}

function updateMetaTags(storeId: string, faviconText: string, storeName: string): void {
  try {
    // Update Open Graph image
    let ogImage = document.querySelector('meta[property="og:image"]') as HTMLMetaElement;
    if (!ogImage) {
      ogImage = document.createElement('meta');
      ogImage.setAttribute('property', 'og:image');
      document.head.appendChild(ogImage);
    }
    ogImage.content = `${import.meta.env.VITE_API_ENDPOINT}/favicon/${storeId}?text=${encodeURIComponent(faviconText)}&bg=%233B82F6&color=%23FFFFFF&size=512&format=png`;

    // Update Twitter card image
    let twitterImage = document.querySelector('meta[name="twitter:image"]') as HTMLMetaElement;
    if (!twitterImage) {
      twitterImage = document.createElement('meta');
      twitterImage.setAttribute('name', 'twitter:image');
      document.head.appendChild(twitterImage);
    }
    twitterImage.content = `${import.meta.env.VITE_API_ENDPOINT}/favicon/${storeId}?text=${encodeURIComponent(faviconText)}&bg=%233B82F6&color=%23FFFFFF&size=512&format=png`;

    // Update site name in meta tags
    let ogTitle = document.querySelector('meta[property="og:title"]') as HTMLMetaElement;
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = storeName;

  } catch (error) {
    console.warn('Failed to update meta tags:', error);
  }
}

export function generateMultipleFaviconSizes(storeName: string, storeDomain?: string): Record<string, string> {
  const faviconText = storeDomain ? 
    storeDomain.split('.')[0] : 
    storeName;

  const sizes = [16, 32, 48, 64, 128, 192, 512];
  const favicons: Record<string, string> = {};

  sizes.forEach(size => {
    try {
      favicons[size.toString()] = generateFavicon({
        text: faviconText,
        backgroundColor: '#3B82F6',
        textColor: '#FFFFFF',
        size
      });
    } catch (error) {
      console.warn(`Failed to generate ${size}x${size} favicon:`, error);
    }
  });

  return favicons;
}
